import * as React from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

type AuthState = {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
};

/**
 * Audit-only: e2e can set `sessionStorage['pwa-audit-slow-auth-ms']` to hold
 * `isLoading === true` long enough to assert OfflineBanner during AuthLoadingShell.
 * Production default: no delay.
 */
async function maybeAuditSlowAuth(): Promise<void> {
  try {
    const raw = sessionStorage.getItem("pwa-audit-slow-auth-ms");
    const ms = raw ? Number(raw) : 0;
    if (Number.isFinite(ms) && ms > 0) {
      await new Promise((r) => setTimeout(r, ms));
    }
  } catch {
    /* ignore */
  }
}

export function useAuth(): AuthState {
  const [state, setState] = React.useState<AuthState>({
    session: null,
    user: null,
    isLoading: true
  });

  React.useEffect(() => {
    let alive = true;

    void (async () => {
      await maybeAuditSlowAuth();
      if (!alive) return;

      try {
        const { data, error } = await supabase.auth.getSession();
        if (!alive) return;
        if (error) {
          setState({ session: null, user: null, isLoading: false });
          return;
        }
        setState({ session: data.session, user: data.session?.user ?? null, isLoading: false });
      } catch {
        if (!alive) return;
        setState({ session: null, user: null, isLoading: false });
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!alive) return;
      // Skip clearing isLoading until slow-auth window ends (first getSession path owns it)
      setState((prev) => {
        if (prev.isLoading) {
          // Keep loading shell until getSession path finishes after audit delay
          return prev;
        }
        return { session, user: session?.user ?? null, isLoading: false };
      });
    });

    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return state;
}
